import { useState } from "react";
import type { KeyboardEvent, ReactElement, ReactNode } from "react";
import type { TicketKalender, TicketBoardCard, TicketSpalte, TicketZeile } from "@kundeninfo/api-client";
import { istWochenendTag, useSofortPopup } from "@kundeninfo/ui";
import { zeileFuer, zeilenText, zielVon, zonenName, freieTage, popupFuerTag, feiertageImBereich, feiertageText,
  tageZwischen } from "./ticketFormat.js";
import type { AblageZiel } from "./ticketFormat.js";
import "./Tickets.css";

/**
 * DIE ZEITLEISTE EINER SPALTE (Spec „Zeitleiste je Spalte"): Jetzt, 30 Tage, 6 Monate, später.
 * Tönung von Wochenende und Feiertag ist Übergang T-3 (Tokens fehlen in me-design, Entscheid 50)
 * — `color-mix` aus vorhandenen Tokens in Tickets.css. Die Zuordnung eines Tickets zu seiner
 * Zeile rechnet jedes Zeichnen neu (keine Datenwanderung, Dirk).
 *
 * Das Sofort-Popup hängt an der BESCHRIFTUNG der Zeile, nicht an der Zeile: `mouseover`/
 * `focusin` steigen auf, und ein Popup an der Zeile überschriebe (oder verbärge, bei leerem
 * Text) das Popup jeder Karte darin, etwa den Steckbrief des Zuständigen (F48). Per Tastatur
 * erreichbar sind die Beschriftungen mit Popup — ein Tabstopp je Spalte (wandernder
 * tabIndex), Pfeil hoch/runter, Pos1/Ende wandern; der Fokus zeigt das Popup.
 */

export type { AblageZiel } from "./ticketFormat.js";

export type TicketZeitleisteProps = {
  spalte: TicketSpalte; zeilen: readonly TicketZeile[]; heute: string; tickets: readonly TicketBoardCard[];
  kalender: readonly TicketKalender[]; offen: boolean;
  karte: (t: TicketBoardCard) => ReactNode;
  zielAttribute: (z: AblageZiel) => Record<string, string>;
};

/** Das Sofort-Popup einer Zeile: freier Tag (Wochenende, Feiertag) oder die Feiertage eines
 *  Monats; leer = keins. Auch für die Datumsspalte des Rasters (Aufgabe 14). */
export function popupVon(z: TicketZeile, kalender: readonly TicketKalender[]): string {
  if (z.art === "TAG") return popupFuerTag(z.datum!, kalender);
  if (z.art !== "MONAT") return "";
  return feiertageImBereich(z.von!, z.bis!, kalender)
    .map((f) => `${f.name} (${f.datum.slice(8, 10)}.${f.datum.slice(5, 7)}.)`).join(", ");
}

function Zeile(p: { z: TicketZeile; tickets: readonly TicketBoardCard[]; props: TicketZeitleisteProps;
                    popup: string; tabstopp: boolean; onFokus: () => void }): ReactElement {
  const { z, tickets, props, popup } = p;
  const feiertag = z.art === "TAG" ? freieTage(props.kalender).get(z.datum!) : undefined;
  const wochenende = z.art === "TAG" && istWochenendTag(z.datum!);
  const imMonat = z.art === "MONAT" ? feiertageImBereich(z.von!, z.bis!, props.kalender) : [];
  const ref = useSofortPopup<HTMLSpanElement>(popup);
  const klassen = ["tz__zeile", `tz__zeile--${z.art.toLowerCase()}`,
    wochenende ? "tz__zeile--wochenende" : "", feiertag !== undefined ? "tz__zeile--feiertag" : "",
    !props.offen && z.art === "TAG" && tickets.length === 0 ? "tz__zeile--leer" : ""]
    .filter((k) => k !== "").join(" ");
  // Entwurf v001 (Sichtprobe 3): die Datumszeile ist ein schmales Band ÜBER den Karten —
  // „+1 T So 18.10.“, „Nov 2026 ab 18.“ —, die Karten nehmen darunter die ganze Spaltenbreite.
  const einheit = z.art === "TAG" ? `+${tageZwischen(props.heute, z.datum!)} T` : null;
  return (
    <li className={klassen} data-zeile={z.index} data-name={zonenName(z)} {...props.zielAttribute(zielVon(props.spalte, z))}>
      <div className="tz__kopf">
        {einheit !== null && <span className="tz__einheit" aria-hidden="true">{einheit}</span>}
        <span ref={ref} className="tz__beschriftung"
          {...(popup !== "" ? { tabIndex: p.tabstopp ? 0 : -1, onFocus: p.onFokus } : {})}>
          {zeilenText(z)}
          {imMonat.length > 0 && (
            <span className="tz__feiertage">{feiertageText(imMonat.length)}</span>
          )}
        </span>
        {tickets.length > 0 && <span className="tz__anzahl" aria-hidden="true">{tickets.length}</span>}
      </div>
      {tickets.length > 0 && <div className="tz__karten">{tickets.map((t) => props.karte(t))}</div>}
    </li>
  );
}

/**
 * Wandernder Tabstopp über Beschriftungen mit Popup (Pfeil hoch/runter, Pos1/Ende): fokussiert
 * die nächste Beschriftung aus `selektor` im Behälter des Ereignisses und gibt sie zurück
 * (null: keine Wandertaste oder kein Stopp). Geteilt von Zeitleiste, Raster und der Zeilenspalte der
 * Zeitachse; `wurzel` weitet den Bereich über den Behälter hinaus (Zeitachse: über alle Gruppen).
 */
export function wandereFokus(e: KeyboardEvent<HTMLElement>, selektor: string,
                             wurzel: ParentNode = e.currentTarget): HTMLElement | null {
  const ziel = e.target as HTMLElement;
  if (!ziel.matches(selektor)) return null;
  const stopps = [...wurzel.querySelectorAll<HTMLElement>(selektor)];
  const i = stopps.indexOf(ziel);
  const j = e.key === "ArrowDown" ? Math.min(i + 1, stopps.length - 1)
    : e.key === "ArrowUp" ? Math.max(i - 1, 0)
    : e.key === "Home" ? 0 : e.key === "End" ? stopps.length - 1 : -1;
  if (i < 0 || j < 0) return null;
  e.preventDefault();
  const naechster = stopps[j]!;
  naechster.focus();
  return naechster;
}

export function TicketZeitleiste(p: TicketZeitleisteProps): ReactElement {
  const [aktiv, setAktiv] = useState<number | null>(null);
  if (p.spalte.timelineMode === "NONE") {
    return (
      <div className="tz tz--ohne" {...p.zielAttribute({ columnId: p.spalte.id, ziel: "KOPF", datum: null,
                                                         text: p.spalte.name })}>
        <div className="tz__kopf">
          <span className="tz__beschriftung">Alle Tickets</span>
          {p.tickets.length > 0 && <span className="tz__anzahl" aria-hidden="true">{p.tickets.length}</span>}
        </div>
        <div className="tz__karten">{p.tickets.map((t) => p.karte(t))}</div>
      </div>
    );
  }
  const gruppen = new Map<number, TicketBoardCard[]>();
  for (const t of p.tickets) {
    const i = zeileFuer(t.resubmitDate, p.zeilen, p.heute);
    gruppen.set(i, [...(gruppen.get(i) ?? []), t]);
  }
  const popups = new Map(p.zeilen.map((z) => [z.index, popupVon(z, p.kalender)]));
  const mitPopup = p.zeilen.filter((z) => popups.get(z.index) !== "").map((z) => z.index);
  const stopp = aktiv !== null && mitPopup.includes(aktiv) ? aktiv : mitPopup[0];
  const tasten = (e: KeyboardEvent<HTMLOListElement>) => {
    const naechster = wandereFokus(e, ".tz__beschriftung[tabindex]");
    if (naechster !== null) setAktiv(Number(naechster.closest("[data-zeile]")?.getAttribute("data-zeile")));
  };
  return (
    <ol className="tz" aria-label={`Zeitleiste ${p.spalte.name}`} onKeyDown={tasten}>
      {p.zeilen.map((z) => (
        <Zeile key={z.index} z={z} tickets={gruppen.get(z.index) ?? []} props={p}
          popup={popups.get(z.index) ?? ""} tabstopp={z.index === stopp} onFokus={() => setAktiv(z.index)} />
      ))}
    </ol>
  );
}
